import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { OrderedSet } from 'immutable';

import { PublicationStatus } from '../../domain/publication-status';
import { ExpositionEndpointMetadataEntity } from '../exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
import { ExpositionEntity } from '../exposition-view/exposition.entity';

interface IExpositionItemEntity {
	id: number;
	label: string;
	datablockSource: string;
	tags: OrderedSet<AppTagItemEntity>;
	tagsArr: Array<string>;
	apiStatus: PublicationStatus;
	accessPointLabel: string;
	accessPointUrl: string;
	columnsCount: number;
	accessCount: number;
	dataEntries: number;
	lastExpositionAction: string | Date | null; // FIXME:
	lastExpositionActionBy: AvatarEntity | null;
	lastElemModification: string | Date | null; // FIXME:
	lastElemModificationBy: AvatarEntity | null;
	duration: string; // FIXME:
	expositionUrl: string;
	sourceUrl: string;
	isApiStatusRunning: boolean;
	actionInProgress: boolean;
	isSelected: boolean;
	endPointMetadata: ExpositionEndpointMetadataEntity;
}

const entityDefaults: IExpositionItemEntity = {
	id: -1,
	label: '',
	datablockSource: '',
	tags: OrderedSet(),
	tagsArr: [],
	apiStatus: PublicationStatus.Unavailable,
	accessPointLabel: '',
	accessPointUrl: '',
	columnsCount: 0,
	accessCount: 0,
	dataEntries: 0,
	lastExpositionAction: null,
	lastExpositionActionBy: null,
	lastElemModification: null,
	lastElemModificationBy: null,
	duration: '',
	expositionUrl: '',
	sourceUrl: '',
	isApiStatusRunning: false,
	actionInProgress: false,
	isSelected: false,
	endPointMetadata: ExpositionEndpointMetadataEntity.build(),
};

export class ExpositionItemEntity implements IExpositionItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public datablockSource = entityDefaults.datablockSource;
	public label = entityDefaults.label;
	public tags = entityDefaults.tags;
	public tagsArr = entityDefaults.tagsArr;
	public accessPointLabel = entityDefaults.accessPointLabel;
	public accessPointUrl = entityDefaults.accessPointUrl;
	public columnsCount = entityDefaults.columnsCount;
	public accessCount = entityDefaults.accessCount;
	public dataEntries = entityDefaults.dataEntries;
	public lastExpositionAction = entityDefaults.lastExpositionAction;
	public lastExpositionActionBy = entityDefaults.lastExpositionActionBy;
	public lastElemModification = entityDefaults.lastElemModification;
	public lastElemModificationBy = entityDefaults.lastElemModificationBy;
	public apiStatus = entityDefaults.apiStatus;
	public duration = entityDefaults.duration;
	public expositionUrl = entityDefaults.expositionUrl;
	public sourceUrl = entityDefaults.sourceUrl;
	public isApiStatusRunning = entityDefaults.isApiStatusRunning;
	public actionInProgress = entityDefaults.actionInProgress;
	public isSelected = entityDefaults.isSelected;
	public endPointMetadata = entityDefaults.endPointMetadata;

	public static build(
		params: Partial<IExpositionItemEntity> = {}
	): ExpositionItemEntity {
		const inst = new ExpositionItemEntity();
		inst.id = params.id ?? entityDefaults.id;
		inst.label = params.label ?? entityDefaults.label;
		inst.datablockSource =
			params.datablockSource ?? entityDefaults.datablockSource;
		inst.tags = params.tags ?? entityDefaults.tags;
		inst.tagsArr =
			params.tags
				?.toList()
				.toArray()
				.map((l) => l.label) ?? entityDefaults.tagsArr;
		inst.accessPointLabel =
			params.accessPointLabel ?? entityDefaults.accessPointLabel;
		inst.accessPointUrl =
			params.accessPointUrl ?? entityDefaults.accessPointUrl;
		inst.columnsCount = params.columnsCount ?? entityDefaults.columnsCount;
		inst.accessCount = params.accessCount ?? entityDefaults.accessCount;
		inst.dataEntries = params.dataEntries ?? entityDefaults.dataEntries;
		inst.lastExpositionAction =
			params.lastExpositionAction ?? entityDefaults.lastExpositionAction;
		inst.lastExpositionActionBy =
			params.lastExpositionActionBy ?? entityDefaults.lastExpositionActionBy;
		inst.lastElemModification =
			params.lastElemModification ?? entityDefaults.lastElemModification;
		inst.lastElemModificationBy =
			params.lastElemModificationBy ?? entityDefaults.lastElemModificationBy;
		inst.apiStatus = params.apiStatus ?? entityDefaults.apiStatus;
		inst.duration = params.duration ?? entityDefaults.duration;
		inst.expositionUrl = params.expositionUrl ?? entityDefaults.expositionUrl;
		inst.sourceUrl = params.sourceUrl ?? entityDefaults.sourceUrl;
		inst.isApiStatusRunning =
			params.isApiStatusRunning ?? entityDefaults.isApiStatusRunning;
		inst.actionInProgress =
			params.actionInProgress ?? entityDefaults.actionInProgress;
		inst.isSelected = params.isSelected ?? entityDefaults.isSelected;
		inst.endPointMetadata =
			params.endPointMetadata ?? entityDefaults.endPointMetadata;
		return inst;
	}

	// the websocket only says a job is running, never which action started it
	public setApiStatusRunning(value: boolean): ExpositionItemEntity {
		return produce(this, (draft: ExpositionItemEntity) => {
			draft.isApiStatusRunning = value;
		});
	}

	public setActionInProgress(value: boolean): ExpositionItemEntity {
		return produce(this, (draft: ExpositionItemEntity) => {
			draft.actionInProgress = value;
		});
	}

	public resetRunningAction(): ExpositionItemEntity {
		return produce(this, (draft: ExpositionItemEntity) => {
			draft.isApiStatusRunning = false;
			draft.actionInProgress = false;
		});
	}

	public toggleSelection(): ExpositionItemEntity {
		return produce(this, (draft: ExpositionItemEntity) => {
			draft.isSelected = !draft.isSelected;
		});
	}

	public updateView(view: ExpositionEntity): ExpositionItemEntity {
		return produce(this, (draft: ExpositionItemEntity) => {
			draft.endPointMetadata = view.endpointMetadata;
		});
	}
}
